/**
 * PlanSelectionModal — plan chooser.
 *
 * Two modes:
 *   • Plan gate (no plan yet): non-dismissible, shows the Free plan + paid plans.
 *   • Upgrade (logged-in free user): dismissible, paid plans only.
 *
 * Paid plans open the PaymentQRModal (UPI QR) — NOT WhatsApp.
 *
 * Props:
 *   open        — boolean
 *   onActivated — called after the free plan is activated
 *   onClose     — called to dismiss (upgrade mode)
 *   dismissible — allow closing (default false)
 *   showFree    — include the Free plan card (default true)
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Bot, Zap, Check, Loader2, Rocket, Building2, Users, X, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import PaymentQRModal from '@/components/PaymentQRModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: 'Free',
    period: '',
    icon: Rocket,
    color: '#00E599',
    highlight: true,
    badge: 'START HERE',
    features: [
      '5 AI generations / month',
      '5 test executions / month',
      'All features included',
      'No credit card required',
    ],
    cta: 'Start Free',
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '₹2,500',
    period: '/month',
    icon: Zap,
    color: '#00E599',
    features: ['Unlimited generations', 'Unlimited executions', 'API testing', 'Priority support'],
    cta: 'Pay with UPI',
  },
  {
    id: 'team',
    name: 'Team',
    price: '₹6,500',
    period: '/month',
    icon: Users,
    color: '#D946EF',
    features: ['Everything in Pro', 'Up to 10 members', 'Team collaboration', 'Shared dashboards'],
    cta: 'Pay with UPI',
  },
  {
    id: 'business',
    name: 'Business',
    price: '₹16,000',
    period: '/month',
    icon: Building2,
    color: '#F59E0B',
    features: ['Everything in Team', 'Unlimited members', 'Dedicated support', 'Custom integrations'],
    cta: 'Pay with UPI',
  },
];

export default function PlanSelectionModal({ open, onActivated, onClose, dismissible = false, showFree = true }) {
  const [loading, setLoading] = useState(false);
  const [qrPlan, setQrPlan] = useState(null); // 'pro' | 'team' | 'business'

  if (!open) return null;

  const plans = showFree ? PLANS : PLANS.filter(p => p.id !== 'free');

  const handlePlan = async (plan) => {
    if (plan.id === 'free') {
      setLoading(true);
      try {
        await axios.post(`${API}/billing/activate-trial`, {}, { withCredentials: true });
        toast.success('Free plan activated! Welcome to AutoQA.');
        onActivated?.();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Could not activate. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      // Show the UPI QR code for this paid plan (no WhatsApp jump).
      setQrPlan(plan.id);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm overflow-y-auto"
      onClick={() => dismissible && onClose?.()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
        className="relative w-full max-w-3xl py-8"
        onClick={e => e.stopPropagation()}
      >
        {dismissible && (
          <button
            onClick={() => onClose?.()}
            className="absolute top-2 right-2 text-slate-500 hover:text-white transition-colors p-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 bg-[#00E599] rounded-sm flex items-center justify-center">
              <Bot className="w-8 h-8 text-black" />
            </div>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            {showFree ? 'Choose your plan' : 'Upgrade your plan'}
          </h1>
          <p className="text-slate-400 text-sm">
            {showFree
              ? 'Start free with 5 generations + 5 runs per month. Upgrade anytime for unlimited usage.'
              : 'Pick a plan and pay securely via UPI QR code.'}
          </p>
        </div>

        {/* Plan cards */}
        <div className={`grid gap-4 ${showFree ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-1 md:grid-cols-3'}`}>
          {plans.map(plan => {
            const Icon = plan.icon;
            return (
              <div
                key={plan.id}
                className={`relative flex flex-col bg-[#0D1117] rounded-2xl p-5 border transition-all ${
                  plan.highlight ? 'border-[#00E599]/50 ring-1 ring-[#00E599]/20' : 'border-white/10 hover:border-white/20'
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap">
                    <span className="bg-[#00E599] text-black text-[10px] font-bold px-2.5 py-0.5 rounded-full">
                      {plan.badge}
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-2 mb-3">
                  <Icon className="w-4 h-4 flex-shrink-0" style={{ color: plan.color }} />
                  <span className="font-semibold text-white text-sm truncate">{plan.name}</span>
                </div>

                <div className="mb-4">
                  <span className="text-xl font-bold text-white">{plan.price}</span>
                  <span className="text-slate-400 text-xs">{plan.period}</span>
                </div>

                <ul className="space-y-1.5 mb-5 flex-1">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-start gap-2 text-[11px] text-slate-300">
                      <Check className="w-3 h-3 text-[#00E599] flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full text-xs h-9 font-semibold ${
                    plan.highlight
                      ? 'bg-[#00E599] text-black hover:bg-[#00CC88]'
                      : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'
                  }`}
                  onClick={() => handlePlan(plan)}
                  disabled={loading && plan.id === 'free'}
                >
                  {loading && plan.id === 'free' ? (
                    <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Activating…</>
                  ) : plan.id !== 'free' ? (
                    <><QrCode className="w-3 h-3 mr-1" /> {plan.cta}</>
                  ) : (
                    plan.cta
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-[11px] text-slate-600 mt-6">
          Paid plans are activated after your UPI payment is verified · Secure &amp; personal
        </p>
      </motion.div>

      {/* UPI QR payment modal for the chosen paid plan */}
      <PaymentQRModal open={!!qrPlan} plan={qrPlan} onClose={() => setQrPlan(null)} />
    </div>
  );
}
